// R-FD-055 / FD-566 — the browser half of the redirect sign-in and the grant return.
//
// Two same-tab round trips through Microsoft's sign-in pages:
//
//   1. SIGN IN & CREATE. The Manager hands back an authorize URL, a PKCE verifier and
//      a state (FD-564). The verifier is held HERE, in sessionStorage keyed by state,
//      for the length of one round trip — then the tab navigates away, Microsoft sends
//      it back to /tenants/connect/callback with a code, and the console posts code +
//      verifier to the Manager, which redeems. The access token never reaches this
//      browser.
//   2. GRANT PERMISSIONS. The server-built grantUrl, with `state` appended so the
//      return to /tenants/consented knows which tenant and which environment.
//
// SESSIONSTORAGE IS THE IN-FLIGHT TRANSACTION, NOT UI STATE. The console's rule is no
// stored UI state (ADR-015, FD-ADR-040 §7). This is the one thing that must survive a
// full-page round trip in THIS tab and nowhere else — exactly what MSAL keeps there
// for the console's own sign-in — and it is single-use: taken (read AND deleted)
// before the redemption call, so a remount cannot redeem twice.
//
// THE ENVIRONMENT TRAVELS WITH IT. Microsoft returns to a bare path with no `?env=`,
// and the active environment decides which Manager the redemption is sent to. A
// multi-environment console that redeemed against its default would create the
// application through the wrong deployment.

import { TenantAppPurpose } from '../api/tenantRegistry';

export type TenantConnectAction = 'create' | 'rotate' | 'remove-previous-key';

export const CONNECT_CALLBACK_PATH = '/tenants/connect/callback';
export const GRANT_RETURN_PATH = '/tenants/consented';

/** How long a stashed sign-in stays redeemable. Entra's code lives ~10 minutes. */
const TTL_MS = 15 * 60 * 1000;
const KEY_PREFIX = 'airbridge.tenantConnect.';

export interface StashedSignIn {
  tenantId: string;
  action: TenantConnectAction;
  codeVerifier: string;
  redirectUri: string;
  environmentId: string;
  createdAt: number;
}

export function purposeForAction(action: TenantConnectAction): TenantAppPurpose {
  return action === 'create' ? 'create' : 'rotate';
}

export function callbackRedirectUri(origin: string = window.location.origin): string {
  return `${origin}${CONNECT_CALLBACK_PATH}`;
}

export function stashSignIn(state: string, value: StashedSignIn): void {
  // A failure here is surfaced by the caller NOT navigating: without the stash the
  // return could never be redeemed, so starting the round trip would waste a sign-in.
  window.sessionStorage.setItem(KEY_PREFIX + state, JSON.stringify(value));
}

/**
 * Reads AND removes the stashed sign-in for `state`. `null` when absent, expired, or
 * unreadable — every one of which means "this tab did not start this sign-in", and
 * the caller must not redeem.
 */
export function takeSignIn(state: string | null, now: number = Date.now()): StashedSignIn | null {
  if (!state) return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KEY_PREFIX + state);
    window.sessionStorage.removeItem(KEY_PREFIX + state);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StashedSignIn;
    if (
      typeof parsed.tenantId !== 'string' ||
      typeof parsed.codeVerifier !== 'string' ||
      typeof parsed.redirectUri !== 'string' ||
      typeof parsed.environmentId !== 'string' ||
      typeof parsed.createdAt !== 'number'
    ) {
      return null;
    }
    if (now - parsed.createdAt > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Peeks without consuming — to restore the environment before redeeming. */
export function peekSignIn(state: string | null): StashedSignIn | null {
  if (!state) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY_PREFIX + state);
    return raw ? (JSON.parse(raw) as StashedSignIn) : null;
  } catch {
    return null;
  }
}

/** The `state` the grant link carries: which tenant, which environment. Not a secret. */
export interface GrantState {
  tenantId: string;
  environmentId: string;
}

export function encodeGrantState(value: GrantState): string {
  return btoa(JSON.stringify({ t: value.tenantId, e: value.environmentId }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function decodeGrantState(state: string | null): GrantState | null {
  if (!state) return null;
  try {
    const padded = state.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded + '==='.slice((padded.length + 3) % 4)));
    if (typeof json?.t !== 'string' || typeof json?.e !== 'string') return null;
    return { tenantId: json.t, environmentId: json.e };
  } catch {
    return null;
  }
}

/** The server's grantUrl with this console's state appended. */
export function grantUrlWithState(grantUrl: string, state: GrantState): string {
  const sep = grantUrl.includes('?') ? '&' : '?';
  return `${grantUrl}${sep}state=${encodeURIComponent(encodeGrantState(state))}`;
}

/**
 * Microsoft's error codes worth naming in plain words. AADSTS50097 is the one FD-559
 * hit live: a tenant that requires device authentication. The portal route always
 * works, so the copy names it rather than leaving the operator at a dead end.
 */
export function describeSignInError(error: string | null, description: string | null): string {
  const text = description ?? '';
  if (/AADSTS50097/.test(text)) {
    return 'This tenant requires the sign-in to come from a device it trusts. Try again from a browser on a managed device, or grant the permissions in the Entra admin center: Enterprise applications → the application → Permissions → Grant admin consent.';
  }
  if (/AADSTS65004|consent_required|access_denied/i.test(`${error ?? ''} ${text}`)) {
    return 'The request was declined, so nothing changed. An administrator of the tenant can try again.';
  }
  const firstLine = text.split(/\r?\n/)[0]?.replace(/\+/g, ' ');
  return firstLine ? `Microsoft returned: ${firstLine}` : `Microsoft returned: ${error ?? 'an error'}`;
}
